import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Chip,
  IconButton,
  Grid,
  Collapse,
  Divider,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from '@mui/material';
import {
  Description as DescriptionIcon,
  CheckCircle as CheckCircleIcon,
  Edit as EditIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  MoreVert as MoreVertIcon,
  AttachFile as AttachFileIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { SoapNote, SoapNoteStatus, SoapPlan } from '../../types/soap';
import TemplateFormRenderer, { TemplateFieldControl } from './TemplateFormRenderer';
import { soapNotesService } from '../../services/soapNotesService';
import {
  INSTANT_FORMATS,
  browserTimeZone,
  calendarDaysUntil,
  formatCalendarDate,
  formatCalendarOrLocalDay,
} from '../../utils/dateUtils';
import { useClinicTimezone, useFormatInstant } from '../../hooks/useClinicTimezone';

/**
 * Produce a readable text summary from a SOAP section that may contain
 * template-specific non-string values (dental chart objects, file arrays, etc.).
 * Skips reserved structural keys and [object Object] values.
 */
function summarizeSectionData(sectionData: Record<string, any> | undefined, maxLength = 100): string | null {
  if (!sectionData) return null;
  const skipKeys = new Set([
    'aiSummary', 'notes', 'findings', 'responses', 'vitals', 'physicalExam',
    'lamenessEval', 'prompts', 'treatments', 'medicationsPrescribed',
    'proceduresPrescribed', 'diagnosticsOrdered', 'followUpDate',
    'followUpInstructions', 'clientEducation', 'planNotes', 'primaryDiagnosis',
    'differentials', 'prognosis',
  ]);
  const parts: string[] = [];
  for (const [key, val] of Object.entries(sectionData)) {
    if (skipKeys.has(key) || key.startsWith('tpl_') || val === undefined || val === null || val === '') continue;
    if (typeof val === 'object' && !Array.isArray(val)) {
      const label = key.replace(/([A-Z])/g, ' $1').replace(/[_-]/g, ' ').trim();
      parts.push(`${label} recorded`);
    } else if (Array.isArray(val) && val.length > 0) {
      if (typeof val[0] === 'object' && val[0]?.fileName) {
        parts.push(`${val.length} file(s) attached`);
      } else {
        parts.push(val.filter(v => typeof v === 'string').join(', '));
      }
    } else if (typeof val === 'string') {
      parts.push(val);
    } else if (typeof val === 'boolean') {
      const label = key.replace(/([A-Z])/g, ' $1').replace(/[_-]/g, ' ').trim();
      parts.push(`${label}: ${val ? 'Yes' : 'No'}`);
    }
  }
  if (parts.length === 0) return null;
  const result = parts.join(', ');
  return result.length > maxLength ? result.substring(0, maxLength) + '...' : result;
}

/** Strip [object Object] from text and replace with something readable */
function cleanObjectStringInText(text: string): string {
  return text.replace(/\[object Object\]/g, '(structured data)');
}

interface SoapNoteCardProps {
  soapNote: SoapNote;
  onView?: (note: SoapNote) => void;
  onEdit?: (note: SoapNote) => void;
  onMenu?: (event: React.MouseEvent<HTMLElement>, noteId: string) => void;
  canEdit?: boolean;
  /** Number of files attached to this note (resolved by the chart from the
   *  Attachment table); shows a paperclip chip on the collapsed card. */
  attachmentCount?: number;
}

/*
 * `plan.followUpDate` is rendered by `dateUtils.formatCalendarOrLocalDay`, and
 * this comment is why the card does not just date-part it.
 *
 * The field is a JSON key stored verbatim, so two populations reach this
 * component. A DAY — `yyyy-MM-ddT12:00:00Z` (or a bare `yyyy-MM-dd`), which is
 * what the web posts and what the server re-spells every zone-free write onto —
 * must be read by its DATE PART and never re-zoned; that is the whole calendar
 * rule, and the `h:mm a` this card used to append beside it was a fabricated
 * time read out of an anchor. A MOMENT — an older iOS build binds a
 * `displayedComponents: [.date]` picker to a value that KEEPS its time of day
 * and posts the whole instant (`SoapNoteFormView.swift:820-827`, `:1106-1108`),
 * so a follow-up picked for Sep 12 at 6:15 PM Pacific arrives as
 * `2026-09-13T01:15:00Z` — must be read as the day it falls on in the VIEWER's
 * zone, because date-parting it prints the NEXT day for every evening entry.
 *
 * The server does NOT collapse the second into the first: that would mean
 * reconstructing the vet's day from `Clinic.timezone`, and a wrong guess is
 * unrecoverable where the client's own value is not (ADR-0034). The read rule
 * lives in one helper instead, and iOS posting a day retires the second
 * population entirely (Docs/handoffs/2026-09-03-ios-calendar-day-anchors.md).
 *
 * A `Date` (the type allows one; the API only ever sends a string) takes the
 * moment arm — it sits at the BROWSER's midnight, so the browser-zone day IS
 * the day the picker showed.
 */

// Module-level cache so we don't re-fetch templates on every card expansion
let templateCachePromise: Promise<any[]> | null = null;
function getCachedTemplates() {
  if (!templateCachePromise) {
    templateCachePromise = soapNotesService.getTemplates().catch(() => {
      templateCachePromise = null;
      return [];
    });
  }
  return templateCachePromise;
}

const SoapNoteCard: React.FC<SoapNoteCardProps> = ({
  soapNote,
  onView,
  onEdit,
  onMenu,
  canEdit = false,
  attachmentCount = 0,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [showTranscript, setShowTranscript] = useState(false);
  const isCompleted = soapNote.status === SoapNoteStatus.COMPLETED;
  // INSTANTS (`SoapNote.date`, `completedAt` — DATE-KINDS §1.2/§1.3) render in
  // the CLINIC zone, never the viewer's browser zone.
  const formatInstant = useFormatInstant();
  // … and the follow-up DAY needs the opposite zone: the viewer's, and only for
  // the legacy iOS instants (see the block above `SoapNoteCardProps`).
  const browserZone = useMemo(() => browserTimeZone(), []);
  // The F/U countdown's "today" is the clinic's again (DATE-KINDS §2.9).
  const clinicZone = useClinicTimezone();

  // Template state for expanded view
  const [templateSchema, setTemplateSchema] = useState<TemplateFieldControl[] | null>(null);
  const [templateValues, setTemplateValues] = useState<Record<string, any>>({});
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateLoadAttempted, setTemplateLoadAttempted] = useState(false);

  // Load template schema when card is expanded for template-based notes
  useEffect(() => {
    if (!expanded || !soapNote.templateUsed || templateLoadAttempted) return;
    setTemplateLoadAttempted(true);
    setTemplateLoading(true);

    getCachedTemplates().then(allTemplates => {
      // Notes name their template; on a tie the clinic's own beats a built-in (isDefault) one.
      const named = allTemplates.filter((t: any) => t.name === soapNote.templateUsed);
      const match = named.find((t: any) => !t.isDefault) ?? named[0];
      if (match?.schema && Array.isArray(match.schema) && match.schema.length > 0) {
        const schema = match.schema as TemplateFieldControl[];
        setTemplateSchema(schema);

        // Reconstruct template values from stored section data
        const restored: Record<string, any> = {};
        schema.forEach((f: TemplateFieldControl) => {
          if (f.type === 'group' && f.children) {
            const groupValues: Record<string, any> = {};
            f.children.forEach((child: TemplateFieldControl) => {
              const sectionData = (soapNote as any)[child.section || f.section];
              if (sectionData?.[child.key] !== undefined) {
                groupValues[child.key] = sectionData[child.key];
              }
            });
            if (Object.keys(groupValues).length > 0) {
              restored[f.key] = groupValues;
            }
          } else {
            const sectionData = (soapNote as any)[f.section];
            if (sectionData?.[f.key] !== undefined) {
              restored[f.key] = sectionData[f.key];
            }
          }
        });
        setTemplateValues(restored);
      }
    }).finally(() => setTemplateLoading(false));
  }, [expanded, soapNote, templateLoadAttempted]);

  const handleTemplateFieldChange = useCallback(() => {}, []);

  const renderPlanTables = () => (
    <>
      {soapNote.plan?.diagnosticsOrdered && soapNote.plan.diagnosticsOrdered.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Diagnostics & Treatments Ordered
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableBody>
                {soapNote.plan.diagnosticsOrdered.map((diagnostic, idx) => (
                  <TableRow key={idx}>
                    <TableCell>{diagnostic}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {((soapNote.plan?.treatments && soapNote.plan.treatments.length > 0) ||
        (soapNote.plan?.medicationsPrescribed && soapNote.plan.medicationsPrescribed.length > 0)) && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Treatments & Medications
          </Typography>
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Treatment/Medication</TableCell>
                  <TableCell>Dosage</TableCell>
                  <TableCell>Frequency</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Route</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {[...(soapNote.plan?.treatments || []), ...(soapNote.plan?.medicationsPrescribed || [])]
                  .filter((item, index, self) => 
                    self.findIndex(t => t.name === item.name) === index
                  )
                  .map((treatment, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{treatment.name}</TableCell>
                      <TableCell>{treatment.dosage || '-'}</TableCell>
                      <TableCell>{treatment.frequency || '-'}</TableCell>
                      <TableCell>{treatment.duration || '-'}</TableCell>
                      <TableCell>{treatment.route || '-'}</TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {soapNote.plan?.followUpDate && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Follow-up
          </Typography>
          <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Typography variant="body2" data-testid="soap-followup-date">
              {formatCalendarOrLocalDay(soapNote.plan.followUpDate, browserZone, 'MMMM d, yyyy')}
            </Typography>
          </Paper>
        </Box>
      )}

      {soapNote.plan?.planNotes && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
            Plan Notes
          </Typography>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem' }}>
              {soapNote.plan.planNotes}
            </Typography>
          </Paper>
        </Box>
      )}
    </>
  );

  // Extract summary information from each section
  const getSubjectiveSummary = () => {
    if (!soapNote.subjective) return null;
    
    // Use AI summary first line if available
    if (soapNote.subjective.aiSummary) {
      const cleaned = cleanObjectStringInText(soapNote.subjective.aiSummary);
      const firstLine = cleaned
        .split('\n')
        .find(line => line.trim() && !line.includes(':'));
      if (firstLine) {
        return firstLine.replace(/^[•\-]\s*/, '').substring(0, 100);
      }
      const preview = cleaned.split('\n')[0] || cleaned;
      return preview.length > 100 ? preview.substring(0, 100) + '...' : preview;
    }
    
    // Fallback to old structure
    const responses = soapNote.subjective.responses || {};
    const chiefComplaint = responses['Chief complaint/reason for visit'];
    if (chiefComplaint || soapNote.subjective.notes) {
      return chiefComplaint || soapNote.subjective.notes;
    }

    // Template data fallback: scan section for readable values
    return summarizeSectionData(soapNote.subjective as any) || 'No subjective data';
  };

  const getObjectiveSummary = () => {
    if (!soapNote.objective) return null;
    
    const vitalsSummary: string[] = [];
    
    // First try to extract from aiSummary (new format)
    if (soapNote.objective.aiSummary) {
      const summary = soapNote.objective.aiSummary;
      
      const tempMatch = summary.match(/Temperature:\s*(\d+\.?\d*)\s*°?F?/i);
      if (tempMatch) {
        const temp = parseFloat(tempMatch[1]);
        vitalsSummary.push(temp < 99 || temp > 101.5 ? `T: ${temp}°F ⚠️` : `T: ${temp}°F`);
      }
      
      const pulseMatch = summary.match(/Pulse:\s*(\d+)\s*bpm/i);
      if (pulseMatch) {
        const pulse = parseInt(pulseMatch[1]);
        vitalsSummary.push(pulse < 28 || pulse > 44 ? `P: ${pulse}bpm ⚠️` : `P: ${pulse}bpm`);
      }
      
      const respMatch = summary.match(/Respiration:\s*(\d+)\s*(?:rpm|\/min)/i);
      if (respMatch) {
        const resp = parseInt(respMatch[1]);
        vitalsSummary.push(resp < 10 || resp > 24 ? `R: ${resp}rpm ⚠️` : `R: ${resp}rpm`);
      }
      
      const weightMatch = summary.match(/Weight:\s*(\d+)\s*kg/i);
      if (weightMatch) {
        vitalsSummary.push(`W: ${weightMatch[1]}kg`);
      }
    } else {
      const vitals = soapNote.objective.vitals;
      if (vitals?.temperature?.value) {
        const temp = vitals.temperature.value;
        vitalsSummary.push(temp < 99 || temp > 101.5 ? `T: ${temp}°F ⚠️` : `T: ${temp}°F`);
      }
      if (vitals?.pulse?.value) {
        const pulse = vitals.pulse.value;
        vitalsSummary.push(pulse < 28 || pulse > 44 ? `P: ${pulse}bpm ⚠️` : `P: ${pulse}bpm`);
      }
      if (vitals?.respiration?.value) {
        const resp = vitals.respiration.value;
        vitalsSummary.push(resp < 10 || resp > 24 ? `R: ${resp}rpm ⚠️` : `R: ${resp}rpm`);
      }
      if (vitals?.weight?.value) {
        vitalsSummary.push(`W: ${vitals.weight.value}kg`);
      }
    }
    
    if (vitalsSummary.length > 0) {
      return vitalsSummary.join(', ');
    }

    // Fallback: Show preview of findings if no vitals were extracted
    const fallbackText = soapNote.objective.aiSummary || soapNote.objective.findings || '';
    if (fallbackText) {
      const cleaned = cleanObjectStringInText(fallbackText);
      const preview = cleaned.split('\n')[0] || cleaned;
      return preview.length > 100 ? preview.substring(0, 100) + '...' : preview;
    }

    // Template data fallback: scan section for readable values
    return summarizeSectionData(soapNote.objective as any) || 'No objective data';
  };

  const getAssessmentSummary = () => {
    if (!soapNote.assessment) return null;
    
    // Use AI summary - extract primary diagnosis line
    if (soapNote.assessment.aiSummary) {
      const cleaned = cleanObjectStringInText(soapNote.assessment.aiSummary);
      const lines = cleaned.split('\n');
      const diagnosisLine = lines.find(line => 
        line.trim() && !line.includes(':') && line.includes('•')
      );
      if (diagnosisLine) {
        return diagnosisLine.replace(/^[•\-]\s*/, '').substring(0, 100);
      }
      const preview = cleaned.split('\n')[0] || cleaned;
      return preview.length > 100 ? preview.substring(0, 100) + '...' : preview;
    }
    
    if (soapNote.assessment.primaryDiagnosis) {
      return soapNote.assessment.primaryDiagnosis;
    }

    if (soapNote.assessment.notes) {
      return soapNote.assessment.notes.length > 100 
        ? soapNote.assessment.notes.substring(0, 100) + '...' 
        : soapNote.assessment.notes;
    }

    // Template data fallback
    return summarizeSectionData(soapNote.assessment as any) || 'No assessment';
  };

  const getPlanSummary = () => {
    if (!soapNote.plan) return null;
    const items = [];
    
    // Show actual diagnostic names (up to 2)
    if (soapNote.plan.diagnosticsOrdered?.length) {
      const diagnostics = soapNote.plan.diagnosticsOrdered.slice(0, 2);
      items.push(diagnostics.join(', '));
      if (soapNote.plan.diagnosticsOrdered.length > 2) {
        items.push(`+${soapNote.plan.diagnosticsOrdered.length - 2} more`);
      }
    }
    
    // Show key medications/treatments (combine both arrays)
    const allTreatments = [
      ...(soapNote.plan.treatments || []),
      ...(soapNote.plan.medicationsPrescribed || [])
    ];
    
    if (allTreatments.length > 0) {
      // Get unique treatment names (first 2)
      const treatmentNames = allTreatments
        .map(t => t.name)
        .filter((name, index, self) => self.indexOf(name) === index)
        .slice(0, 2);
      items.push(treatmentNames.join(', '));
      if (allTreatments.length > 2) {
        items.push(`+${allTreatments.length - 2} meds`);
      }
    }
    
    // Show follow-up date prominently. It is a CALENDAR day, so "days until" is
    // the calendar-day difference from the CLINIC's today (DATE-KINDS §2.9) —
    // instant arithmetic on the stored anchor called a noon-anchored "today"
    // Tomorrow before 7 AM Eastern, and a midnight-anchored "tomorrow" Today
    // after 7 PM. Only "today" is the clinic's: WHICH day the value is stays the
    // viewer's call (ADR-0034). 'yyyy-MM-dd' asks the same helper for the
    // resolved DAY rather than a second copy of its rule, so the chip and the
    // block below can never disagree about which day a value means.
    const followUpYmd = formatCalendarOrLocalDay(
      soapNote.plan.followUpDate,
      browserZone,
      'yyyy-MM-dd',
    );
    const daysUntil = calendarDaysUntil(followUpYmd, clinicZone);
    if (daysUntil !== null) {
      if (daysUntil === 0) {
        items.push(`F/U: Today`);
      } else if (daysUntil === 1) {
        items.push(`F/U: Tomorrow`);
      } else if (daysUntil > 0 && daysUntil <= 7) {
        items.push(`F/U: ${daysUntil} days`);
      } else {
        items.push(`F/U: ${formatCalendarDate(followUpYmd, 'MMM d')}`);
      }
    }
    
    if (items.length > 0) {
      return items.join(', ');
    }

    // Fallback: Show preview of plan notes if no structured items
    const planText = soapNote.plan.planNotes || soapNote.plan.notes || '';
    if (planText) {
      const preview = planText.split('\n')[0] || planText;
      return preview.length > 100 ? preview.substring(0, 100) + '...' : preview;
    }

    return 'No plan';
  };

  return (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box flex={1}>
            {/* Header */}
            <Box display="flex" alignItems="center" gap={2} mb={1}>
              <Box display="flex" alignItems="center" gap={1}>
                <DescriptionIcon color="primary" />
                <Typography variant="h6">
                  SOAP Note
                </Typography>
              </Box>
              <Chip
                label={isCompleted ? 'Completed' : 'Draft'}
                size="small"
                color={isCompleted ? 'success' : 'warning'}
                icon={isCompleted ? <CheckCircleIcon /> : undefined}
              />
              {soapNote.templateUsed && (
                <Chip
                  label={soapNote.templateUsed}
                  size="small"
                  variant="outlined"
                />
              )}
              {attachmentCount > 0 && (
                <Chip
                  icon={<AttachFileIcon />}
                  label={attachmentCount}
                  size="small"
                  variant="outlined"
                  color="primary"
                  title={`${attachmentCount} attached file${attachmentCount === 1 ? '' : 's'}`}
                  sx={{ fontWeight: 600 }}
                />
              )}
            </Box>

            {/* Date and Provider */}
            <Typography variant="body2" color="textSecondary" gutterBottom>
              <span data-testid="soap-note-date">
                {formatInstant(soapNote.date, INSTANT_FORMATS.dateTimeLong)}
              </span> • {' '}
              {/* formatDistanceToNow diffs two instants — zone-agnostic, correct as-is (DATE-KINDS §2.3). */}
              {formatDistanceToNow(new Date(soapNote.date), { addSuffix: true })}
              {soapNote.provider && (
                <> • Dr. {soapNote.provider.name}</>
              )}
            </Typography>

            {/* SOAP Summary Grid */}
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary">
                    Subjective
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {getSubjectiveSummary()}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary">
                    Objective
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {getObjectiveSummary()}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary">
                    Assessment
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    {getAssessmentSummary()}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="subtitle2" fontWeight="bold" color="primary">
                    Plan
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 0.5 }} data-testid="soap-plan-summary">
                    {getPlanSummary()}
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {/* Completed Date */}
            {isCompleted && soapNote.completedAt && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                Completed on{' '}
                <span data-testid="soap-completed-at">
                  {formatInstant(soapNote.completedAt, INSTANT_FORMATS.dateTime)}
                </span>
              </Typography>
            )}
          </Box>

          {/* Actions */}
          <Box display="flex" gap={0.5}>
            <IconButton
              size="small"
              onClick={() => setExpanded(!expanded)}
              title={expanded ? "Collapse" : "Expand"}
            >
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
            {canEdit && !isCompleted && (
              <IconButton
                size="small"
                onClick={() => onEdit?.(soapNote)}
                title="Edit SOAP Note"
              >
                <EditIcon />
              </IconButton>
            )}
            {canEdit && onMenu && (
              <IconButton
                size="small"
                onClick={(e) => onMenu(e, soapNote.id)}
                title="More options"
              >
                <MoreVertIcon />
              </IconButton>
            )}
          </Box>
        </Box>

        {/* Expanded Content */}
        <Collapse in={expanded} timeout="auto" unmountOnExit>
          <Divider sx={{ mt: 2, mb: 2 }} />
          
          {/* Original Transcript - Toggle Button */}
          {soapNote.originalTranscript && (
            <Box sx={{ mb: 3 }}>
              <Button
                size="small"
                variant="outlined"
                onClick={() => setShowTranscript(!showTranscript)}
                sx={{ mb: 1 }}
              >
                {showTranscript ? 'Hide Original Transcript' : 'Show Original Transcript'}
              </Button>
              <Collapse in={showTranscript}>
                <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50', mt: 1 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {soapNote.originalTranscript}
                  </Typography>
                </Paper>
              </Collapse>
            </Box>
          )}

          {/* Template-based rendering when schema is available */}
          {templateLoading && (
            <Box display="flex" justifyContent="center" py={3}>
              <CircularProgress size={24} />
            </Box>
          )}

          {templateSchema ? (
            <>
              <Box sx={{
                '& .MuiInputBase-input.Mui-disabled': {
                  color: 'rgba(0,0,0,0.87) !important',
                  WebkitTextFillColor: 'rgba(0,0,0,0.87) !important',
                },
                '& .MuiInputLabel-root.Mui-disabled': {
                  color: 'rgba(0,0,0,0.6) !important',
                },
                '& .MuiFormControlLabel-label.Mui-disabled': {
                  color: 'rgba(0,0,0,0.87) !important',
                },
                '& .MuiCheckbox-root.Mui-disabled': {
                  color: 'rgba(0,0,0,0.6) !important',
                },
                '& .MuiSwitch-root .Mui-disabled': {
                  color: 'rgba(0,0,0,0.6) !important',
                },
                '& .MuiSelect-select.Mui-disabled': {
                  color: 'rgba(0,0,0,0.87) !important',
                  WebkitTextFillColor: 'rgba(0,0,0,0.87) !important',
                },
              }}>
                <TemplateFormRenderer
                  schema={templateSchema}
                  values={templateValues}
                  onChange={handleTemplateFieldChange}
                  readOnly
                />
              </Box>

              {/* Plan structured tables below the template form */}
              {((soapNote.plan?.diagnosticsOrdered && soapNote.plan.diagnosticsOrdered.length > 0) ||
                (soapNote.plan?.treatments && soapNote.plan.treatments.length > 0) ||
                (soapNote.plan?.medicationsPrescribed && soapNote.plan.medicationsPrescribed.length > 0) ||
                soapNote.plan?.followUpDate || soapNote.plan?.planNotes) && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 2 }} />
                  {renderPlanTables()}
                </Box>
              )}
            </>
          ) : !templateLoading && (
            <>
              {/* Legacy view: plain text sections */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                  Subjective
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {cleanObjectStringInText(soapNote.subjective?.aiSummary || soapNote.subjective?.notes || 'No subjective data')}
                  </Typography>
                </Paper>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                  Objective
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {cleanObjectStringInText(soapNote.objective?.aiSummary || soapNote.objective?.findings || 'No objective data')}
                  </Typography>
                </Paper>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                  Assessment
                </Typography>
                <Paper variant="outlined" sx={{ p: 2 }}>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '0.85rem' }}>
                    {cleanObjectStringInText(soapNote.assessment?.aiSummary || soapNote.assessment?.notes || 'No assessment data')}
                  </Typography>
                </Paper>
              </Box>

              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                  Plan
                </Typography>
                {renderPlanTables()}
              </Box>
            </>
          )}

          {/* Edit Button at Bottom */}
          {canEdit && !isCompleted && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="contained"
                startIcon={<EditIcon />}
                onClick={() => onEdit?.(soapNote)}
              >
                Edit SOAP Note
              </Button>
            </Box>
          )}
        </Collapse>
      </CardContent>
    </Card>
  );
};

export default SoapNoteCard;
